import React, {FC} from 'react';

import {KeyboardActionsEnum} from '../../../../../../libs/shared/core/src/helpers/browser/keyboardHelpers';
import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {RendererOf} from '../../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {CellsContext} from '../../../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {ViewerPanelId} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {ToolbarButton} from '../../../../../../libs/shared/ui/src/components/buttons/toolbarButton';
import {UserThemeConsumer} from '../../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {PermissionDenied} from '../../../../../../srcCommon/permissions/permissionDenied';
import {computeIsAnyConversationDraftLater} from '../../../../../helpers/models/conversationHelpers';
import {ConversationBaseModel} from '../../../../../models/api/conversationModel';
import {InboxesParameters} from '../../../../../store/panels/inboxes/parameters/inboxesParameters';
import {
  ConversationsTrashButtonProps,
  ConversationsTrashCoordinatorModesEnum,
} from '../actions/trash/conversationsTrashCoordinator';
import {ConversationsTrashCoordinatorContainer} from '../actions/trash/conversationsTrashCoordinatorContainer';

/*
 * Props.
 */

interface ConversationTopbarTrashProps {
  panelId: ViewerPanelId;
  cellsContext: CellsContext;
  inboxesParameters: InboxesParameters;
  conversations: ReadonlyArray<ConversationBaseModel>;
  isBulkActionRequired: boolean;
  isTooltipMultiline?: boolean;
  renderTooltip?: RendererOf<ConversationsTrashButtonProps>;
}

/*
 * Component.
 */

export const ConversationTopbarTrash: FC<ConversationTopbarTrashProps> = (props) => (
  <ConversationsTrashCoordinatorContainer
    cellsContext={props.cellsContext}
    inboxesParameters={props.inboxesParameters}
    conversations={props.conversations}
    isBulkActionRequired={props.isBulkActionRequired}
    renderButton={(buttonProps) => renderButton(buttonProps, props)}
    panelId={props.panelId}
    skipTopbarPage
    mode={ConversationsTrashCoordinatorModesEnum.TRASH}
  />
);

/*
 * Helpers.
 */

function renderButton(buttonProps: ConversationsTrashButtonProps, props: ConversationTopbarTrashProps) {
  const {onClick, permissionResult, areAllTrashed, areAllSpam} = buttonProps;
  const isAnyConversationDraftLater = computeIsAnyConversationDraftLater(props.conversations);

  const renderTooltip = props.renderTooltip ? () => props.renderTooltip?.(buttonProps) : undefined;
  if (!permissionResult.isAllowed) {
    return (
      <ToolbarButton
        noMargin
        isDisabled
        iconName="trash"
        label={<Trans id="toolbar-trash">Trash</Trans>}
        renderTooltip={() => <PermissionDenied reason={permissionResult.reason} />}
      />
    );
  }

  // If the conversation is marked as spam, render the filled icon.
  if (areAllSpam) {
    return (
      <UserThemeConsumer>
        {(theme) => (
          <ToolbarButton
            noMargin
            onClick={onClick}
            iconName="spamFilled"
            color={theme.palette.purple.shade40}
            hoverColor={theme.palette.purple.shade50}
            keyboardAction={KeyboardActionsEnum.SPAM}
            label={<Trans id="toolbar-restore">Restore</Trans>}
            isTooltipMultiline={props.isTooltipMultiline}
            renderTooltip={renderTooltip}
            placement="bottom"
          />
        )}
      </UserThemeConsumer>
    );
  }

  // If the conversation is trashed, render the filled icon.
  if (areAllTrashed) {
    return (
      <UserThemeConsumer>
        {(theme) => (
          <ToolbarButton
            noMargin
            onClick={onClick}
            iconName="trashFilled"
            color={theme.palette.red.shade40}
            hoverColor={theme.palette.red.shade50}
            keyboardAction={KeyboardActionsEnum.TRASH}
            label={<Trans id="toolbar-restore">Restore</Trans>}
            isTooltipMultiline={props.isTooltipMultiline}
            renderTooltip={renderTooltip}
            placement="bottom"
          />
        )}
      </UserThemeConsumer>
    );
  }

  // Otherwise, render the normal icon.
  return (
    <UserThemeConsumer>
      {(theme) => (
        <ToolbarButton
          isDisabled={isAnyConversationDraftLater}
          noMargin
          onClick={onClick}
          iconName="trash"
          keyboardAction={KeyboardActionsEnum.TRASH}
          label={<Trans id="toolbar-trash">Trash</Trans>}
          color={theme.greys.shade80}
          hoverColor={theme.greys.shade80}
          isTooltipMultiline={props.isTooltipMultiline}
          renderTooltip={renderTooltip}
          placement="bottom"
        />
      )}
    </UserThemeConsumer>
  );
}
